import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import apiClient from '../utils/api';
import { renderKitPreview, getKitStyleById } from '../utils/kitUtils';

interface LeaderboardEntry {
  user: {
    id: string;
    teamName: string;
    kitStyle?: string;
    kitPrimaryColour?: string;
    kitSecondaryColour?: string;
  };
  totalPoints: number;
  exactScoreCount: number;
  correctResultCount: number;
  incorrectCount: number;
  position: number;
}

interface Season {
  id: string;
  name: string;
  startDate: string;
  endDate: string;
  gameType: string;
  competitionId?: string | null;
  order?: number;
}

interface UserLeagueSeason {
  endDate?: string | null;
}

interface UserLeague {
  id: string;
  name: string;
  isActive: boolean;
  season?: UserLeagueSeason | null;
}

const addOneMonth = (date: Date): Date => {
  const result = new Date(date);
  result.setMonth(result.getMonth() + 1);
  return result;
};

/** Visible in widget: league active and within 1 month after season end. */
const isLeagueWidgetActive = (league: UserLeague): boolean => {
  if (!league.isActive) return false;

  const endDate = league.season?.endDate;
  if (!endDate) return true;

  const inactiveAfter = addOneMonth(new Date(endDate));
  return Date.now() <= inactiveAfter.getTime();
};

/** Season ended but still inside the post-season grace window. */
const isLeagueFinished = (league: UserLeague): boolean => {
  const endDate = league.season?.endDate;
  if (!endDate) return false;
  return Date.now() > new Date(endDate).getTime();
};

interface PartialLeaderboardResponse {
  leaderboard: LeaderboardEntry[];
  currentUserPosition: LeaderboardEntry | null;
  showPartial: boolean;
  filters: {
    season?: string;
    view?: 'monthly' | 'season';
    year?: number;
    month?: number;
    leagueId?: string;
  };
}

const parseSelectedTab = (tab: string): { scope: 'global' | 'league'; id: string } | null => {
  const [scope, id] = tab.split(':');
  if ((scope === 'global' || scope === 'league') && id) {
    return { scope, id };
  }
  return null;
};

const buildSelectedTab = (scope: 'global' | 'league', id: string) => `${scope}:${id}`;

const CACHE_TTL_MS = 15 * 60 * 1000;

const PartialLeaderboardWidget: React.FC = () => {
  const { user } = useAuth();
  const [leaderboardData, setLeaderboardData] = useState<PartialLeaderboardResponse | null>(null);
  const [seasons, setSeasons] = useState<Season[]>([]);
  const [participatedSeasonIds, setParticipatedSeasonIds] = useState<string[]>([]);
  const [userLeagues, setUserLeagues] = useState<UserLeague[]>([]);
  const [setupReady, setSetupReady] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [selectedTab, setSelectedTab] = useState('');

  const [seasonTabsScrollLeft, setSeasonTabsScrollLeft] = useState(0);
  const fetchGenerationRef = useRef(0);

  const handleSeasonTabsScroll = (e: React.UIEvent<HTMLDivElement>) => {
    setSeasonTabsScrollLeft(e.currentTarget.scrollLeft);
  };

  const visibleSeasons = useMemo(() => {
    const participated = new Set(participatedSeasonIds);
    const latestByCompetition = new Map<string, Season>();

    seasons
      .filter((season) => participated.has(season.id))
      .forEach((season) => {
        const key = season.competitionId || season.id;
        const existing = latestByCompetition.get(key);
        if (!existing || new Date(season.startDate) > new Date(existing.startDate)) {
          latestByCompetition.set(key, season);
        }
      });

    return Array.from(latestByCompetition.values()).sort(
      (a, b) => (a.order || 0) - (b.order || 0)
    );
  }, [seasons, participatedSeasonIds]);

  const showAllCompetitionsTab = visibleSeasons.length >= 2;
  const activeUserLeagues = useMemo(
    () => userLeagues.filter(league => isLeagueWidgetActive(league) && !isLeagueFinished(league)),
    [userLeagues]
  );
  const hasGlobalTabs = visibleSeasons.length > 0;
  const hasLeagueTabs = activeUserLeagues.length > 0;
  const totalTabCount =
    visibleSeasons.length +
    (showAllCompetitionsTab ? 1 : 0) +
    activeUserLeagues.length;
  const selectedTabInfo = parseSelectedTab(selectedTab);
  const isLeagueTab = selectedTabInfo?.scope === 'league';

  // Seasons, participated season IDs, and user leagues in parallel (lightweight)
  useEffect(() => {
    if (!user) {
      setSetupReady(true);
      return;
    }

    const fetchSetup = async () => {
      const seasonsCacheKey = 'partial_leaderboard_seasons_data';
      const seasonsTimestampKey = 'partial_leaderboard_seasons_timestamp';
      const participatedCacheKey = `partial_leaderboard_participated_${user.id}`;
      const participatedTimestampKey = `partial_leaderboard_participated_ts_${user.id}`;
      const userLeaguesCacheKey = `partial_leaderboard_user_leagues_v2_${user.id}`;
      const userLeaguesTimestampKey = `partial_leaderboard_user_leagues_v2_ts_${user.id}`;

      const readCache = <T,>(dataKey: string, tsKey: string): T | null => {
        const raw = sessionStorage.getItem(dataKey);
        const ts = sessionStorage.getItem(tsKey);
        if (!raw || !ts) return null;
        if (Date.now() - parseInt(ts, 10) >= CACHE_TTL_MS) {
          sessionStorage.removeItem(dataKey);
          sessionStorage.removeItem(tsKey);
          return null;
        }
        try {
          return JSON.parse(raw) as T;
        } catch {
          sessionStorage.removeItem(dataKey);
          sessionStorage.removeItem(tsKey);
          return null;
        }
      };

      const cachedSeasons = readCache<Season[]>(seasonsCacheKey, seasonsTimestampKey);
      const cachedParticipated = readCache<string[]>(participatedCacheKey, participatedTimestampKey);
      const cachedUserLeagues = readCache<UserLeague[]>(userLeaguesCacheKey, userLeaguesTimestampKey);

      if (cachedSeasons) setSeasons(cachedSeasons);
      if (cachedParticipated) setParticipatedSeasonIds(cachedParticipated);
      if (cachedUserLeagues) setUserLeagues(cachedUserLeagues);

      if (cachedSeasons && cachedParticipated && cachedUserLeagues) {
        setSetupReady(true);
        return;
      }

      try {
        const [seasonsRes, participatedRes, userLeaguesRes] = await Promise.all([
          cachedSeasons ? Promise.resolve({ data: cachedSeasons }) : apiClient.get('/seasons'),
          cachedParticipated
            ? Promise.resolve({ data: cachedParticipated })
            : apiClient.get('/predictions/participated-seasons'),
          cachedUserLeagues
            ? Promise.resolve({ data: cachedUserLeagues })
            : apiClient.get('/leagues/my'),
        ]);

        if (!cachedSeasons) {
          setSeasons(seasonsRes.data);
          sessionStorage.setItem(seasonsCacheKey, JSON.stringify(seasonsRes.data));
          sessionStorage.setItem(seasonsTimestampKey, Date.now().toString());
        }

        if (!cachedParticipated) {
          setParticipatedSeasonIds(participatedRes.data || []);
          sessionStorage.setItem(participatedCacheKey, JSON.stringify(participatedRes.data || []));
          sessionStorage.setItem(participatedTimestampKey, Date.now().toString());
        }

        if (!cachedUserLeagues) {
          const leagues = (userLeaguesRes.data || [])
            .map((membership: { league?: UserLeague & { season?: UserLeagueSeason } }) => {
              if (!membership.league) return null;
              return {
                id: membership.league.id,
                name: membership.league.name,
                isActive: membership.league.isActive,
                season: membership.league.season,
              };
            })
            .filter((league: UserLeague | null): league is UserLeague => !!league);
          setUserLeagues(leagues);
          sessionStorage.setItem(userLeaguesCacheKey, JSON.stringify(leagues));
          sessionStorage.setItem(userLeaguesTimestampKey, Date.now().toString());
        }
      } catch (err) {
        console.error('Error loading partial leaderboard setup:', err);
      } finally {
        setSetupReady(true);
      }
    };

    fetchSetup();
  }, [user]);

  useEffect(() => {
    if (!setupReady) return;

    if (!hasGlobalTabs && !hasLeagueTabs) {
      setLoading(false);
      return;
    }

    const validTabs = new Set<string>([
      ...visibleSeasons.map((season) => buildSelectedTab('global', season.id)),
      ...(showAllCompetitionsTab ? [buildSelectedTab('global', 'all')] : []),
      ...activeUserLeagues.map((league) => buildSelectedTab('league', league.id)),
    ]);

    if (!selectedTab || !validTabs.has(selectedTab)) {
      if (activeUserLeagues[0]) {
        setSelectedTab(buildSelectedTab('league', activeUserLeagues[0].id));
      } else if (hasGlobalTabs) {
        setSelectedTab(buildSelectedTab('global', visibleSeasons[0].id));
      }
    }
  }, [setupReady, visibleSeasons, showAllCompetitionsTab, selectedTab, hasGlobalTabs, hasLeagueTabs, activeUserLeagues]);

  useEffect(() => {
    if (!user || !selectedTab || !setupReady || (!hasGlobalTabs && !hasLeagueTabs)) return;

    const tabInfo = parseSelectedTab(selectedTab);
    if (!tabInfo) return;

    const currentFilters = isLeagueTab
      ? `league-${tabInfo.id}`
      : `global-${tabInfo.id}-season`;
    const cacheKey = `partial_leaderboard_${currentFilters}`;
    const cacheTimestampKey = `partial_leaderboard_timestamp_${currentFilters}`;
    const generation = ++fetchGenerationRef.current;

    const readPartialCache = (): PartialLeaderboardResponse | null => {
      const raw = sessionStorage.getItem(cacheKey);
      const ts = sessionStorage.getItem(cacheTimestampKey);
      if (!raw || !ts) return null;
      if (Date.now() - parseInt(ts, 10) >= CACHE_TTL_MS) {
        sessionStorage.removeItem(cacheKey);
        sessionStorage.removeItem(cacheTimestampKey);
        return null;
      }
      try {
        return JSON.parse(raw) as PartialLeaderboardResponse;
      } catch {
        sessionStorage.removeItem(cacheKey);
        sessionStorage.removeItem(cacheTimestampKey);
        return null;
      }
    };

    const cached = readPartialCache();
    if (cached) {
      setLeaderboardData(cached);
      setLoading(false);
      setError(null);
    } else {
      setLoading(true);
      setError(null);
    }

    const fetchPartialLeaderboard = async () => {
      try {
        let response;

        if (tabInfo.scope === 'league') {
          response = await apiClient.get(`/leagues/${tabInfo.id}/standings/partial`);
        } else {
          const params = new URLSearchParams({
            season: tabInfo.id,
            view: 'season',
          });

          response = await apiClient.get(`/leaderboards/partial?${params}`);
        }

        if (generation !== fetchGenerationRef.current) return;

        setLeaderboardData(response.data);
        sessionStorage.setItem(cacheKey, JSON.stringify(response.data));
        sessionStorage.setItem(cacheTimestampKey, Date.now().toString());
      } catch (err) {
        if (generation !== fetchGenerationRef.current) return;
        if (!cached) {
          setError('Failed to load leaderboard data');
        }
        console.error('Error fetching partial leaderboard:', err);
      } finally {
        if (generation === fetchGenerationRef.current) {
          setLoading(false);
        }
      }
    };

    fetchPartialLeaderboard();
  }, [selectedTab, user, setupReady, hasGlobalTabs, hasLeagueTabs, isLeagueTab]);

  const isCurrentUser = (entry: LeaderboardEntry) => {
    return user && entry.user.id === user.id;
  };

  if (!user) return null;
  if (setupReady && !hasGlobalTabs && !hasLeagueTabs) return null;

  if (loading && !leaderboardData) {
    return (
      <div className="max-w-5xl mx-auto mt-8 px-2 md:px-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-300 dark:bg-gray-600 rounded mb-6 w-48"></div>
          <div className="card bg-gray-50 dark:bg-gray-800/50 p-6">
            <div className="flex flex-wrap gap-2 mb-6">
              <div className="h-10 bg-gray-300 dark:bg-gray-600 rounded w-28"></div>
              <div className="h-10 bg-gray-300 dark:bg-gray-600 rounded w-36"></div>
              <div className="h-10 bg-gray-300 dark:bg-gray-600 rounded w-32"></div>
            </div>
            <div className="space-y-3">
              {[...Array(11)].map((_, i) => (
                <div key={i} className="h-12 bg-gray-300 dark:bg-gray-600 rounded"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error && !leaderboardData) {
    return (
      <div className="max-w-5xl mx-auto mt-8 px-2 md:px-6">
        <div className="mb-6">
          <h3 className="text-xl font-bold mb-2" style={{ color: 'var(--text-primary)' }}>
            My Leaderboard Position
          </h3>
        </div>
        <div className="card bg-gray-50 dark:bg-gray-800/50 p-6 text-center">
          <p style={{ color: 'var(--text-secondary)' }}>{error}</p>
        </div>
      </div>
    );
  }

  if (leaderboardData && !leaderboardData.showPartial && !isLeagueTab) {
    return null;
  }

  return (
    <div className="max-w-5xl mx-auto mt-8">
        <div className="mb-4">
          <h3 className="text-xl font-bold mb-2" style={{ color: 'var(--text-primary)' }}>
            My Leaderboard Position
          </h3>
        </div>

      <div className="bg-light-surface dark:bg-dark-surface rounded-lg mb-4">

        <div className="py-4 pb-2">
          <div className="relative">
            {totalTabCount > 4 && seasonTabsScrollLeft > 0 && (
              <button
                onClick={() => {
                  const container = document.getElementById('partial-season-tabs');
                  if (container) {
                    container.scrollBy({ left: -200, behavior: 'smooth' });
                  }
                }}
                className="absolute left-0 top-0 bottom-0 z-10 w-8 bg-gradient-to-r from-[#eff5f6] to-transparent dark:from-gray-900 dark:to-transparent flex items-center justify-center opacity-80 hover:opacity-100 transition-opacity"
                style={{ marginLeft: '-8px' }}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
            )}

            <div
              id="partial-season-tabs"
              className="flex gap-2 overflow-x-auto scrollbar-hide pr-8"
              onScroll={handleSeasonTabsScroll}
            >
              {activeUserLeagues.map((league) => (
                <button
                  key={league.id}
                  onClick={() => setSelectedTab(buildSelectedTab('league', league.id))}
                  className={`px-4 py-2 rounded-md text-sm font-medium whitespace-nowrap flex-shrink-0 ${
                    selectedTab === buildSelectedTab('league', league.id)
                      ? 'btn-primary'
                      : 'btn-secondary'
                  }`}
                  title={league.name}
                >
                  {league.name}
                </button>
              ))}
              {hasGlobalTabs && hasLeagueTabs && (
                <div
                  className="w-px bg-light-border dark:bg-dark-border flex-shrink-0 self-stretch mx-1"
                  aria-hidden="true"
                />
              )}
              {visibleSeasons.map(season => (
                <button
                  key={season.id}
                  onClick={() => setSelectedTab(buildSelectedTab('global', season.id))}
                  className={`px-4 py-2 rounded-md text-sm font-medium whitespace-nowrap flex-shrink-0 ${
                    selectedTab === buildSelectedTab('global', season.id)
                      ? 'btn-primary'
                      : 'btn-secondary'
                  }`}
                >
                  {season.name}
                </button>
              ))}
              {showAllCompetitionsTab && (
                <button
                  onClick={() => setSelectedTab(buildSelectedTab('global', 'all'))}
                  className={`px-4 py-2 rounded-md text-sm font-medium whitespace-nowrap flex-shrink-0 ${
                    selectedTab === buildSelectedTab('global', 'all')
                      ? 'btn-primary'
                      : 'btn-secondary'
                  }`}
                >
                  All Competitions
                </button>
              )}
            </div>

            {totalTabCount > 4 && (
              <button
                onClick={() => {
                  const container = document.getElementById('partial-season-tabs');
                  if (container) {
                    container.scrollBy({ left: 200, behavior: 'smooth' });
                  }
                }}
                className="absolute right-0 top-0 bottom-0 z-10 w-8 bg-gradient-to-l from-[#eff5f6] to-transparent dark:from-gray-900 dark:to-transparent flex items-center justify-center opacity-80 hover:opacity-100 transition-opacity"
                style={{ marginRight: '-8px' }}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            )}
          </div>
        </div>

      </div>

      <div className="overflow-hidden">

        {loading && leaderboardData && (
          <p className="text-xs text-center mb-2" style={{ color: 'var(--text-secondary)' }}>
            Updating…
          </p>
        )}

        {leaderboardData && leaderboardData.leaderboard.length > 0 ? (
          <div className="overflow-x-auto overflow-y-hidden card shadow bg-gray-50 dark:bg-gray-800/30">
            <table className="w-full">
              <thead className="bg-light-bg dark:bg-dark-bg">
                <tr>
                  <th className="pl-6 pr-1 py-3 text-left text-xs font-medium text-light-text-secondary dark:text-dark-text-secondary uppercase tracking-wider">
                    Position
                  </th>
                  <th className="px-2 py-3 text-left text-xs font-medium text-light-text-secondary dark:text-dark-text-secondary uppercase tracking-wider">
                    Team
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-light-text-secondary dark:text-dark-text-secondary uppercase tracking-wider">
                    Total Points
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-light-text-secondary dark:text-dark-text-secondary uppercase tracking-wider">
                    Correct Results
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-light-text-secondary dark:text-dark-text-secondary uppercase tracking-wider">
                    Exact Scores
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-light-border dark:divide-dark-border">
                {leaderboardData.leaderboard.map((entry) => (
                  <tr
                    key={entry.user.id}
                    className={`${
                      isCurrentUser(entry)
                        ? 'bg-highlight/10 border-l-4 border-highlight'
                        : 'hover:bg-light-bg dark:hover:bg-dark-bg'
                    } transition-colors`}
                  >
                    <td className="pl-6 pr-1 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <span className={`text-sm font-medium ${
                          entry.position <= 3
                            ? 'text-highlight'
                            : 'text-light-text dark:text-dark-text'
                        }`}>
                          {entry.position}
                        </span>
                        {entry.position === 1 && <span className="ml-2 text-yellow-500">🏆</span>}
                        {entry.position === 2 && <span className="ml-2 text-gray-400">🥈</span>}
                        {entry.position === 3 && <span className="ml-2 text-orange-600">🥉</span>}
                      </div>
                    </td>
                    <td className="px-2 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 rounded flex items-center justify-center">
                          {(() => {
                            const kitStyle = entry.user.kitStyle
                              ? getKitStyleById(entry.user.kitStyle)
                              : getKitStyleById('solid');
                            if (kitStyle) {
                              return renderKitPreview(
                                kitStyle,
                                entry.user.kitPrimaryColour || '#7e8c82',
                                entry.user.kitSecondaryColour || '#ffffff',
                                'small'
                              );
                            }
                            return null;
                          })()}
                        </div>
                        <div className="text-sm font-medium text-light-text dark:text-dark-text">
                          <Link
                            to={`/users/${entry.user.id}`}
                            className="text-sm font-medium hover:underline"
                            style={{ color: 'var(--text-primary)' }}
                          >
                            {entry.user.teamName}
                          </Link>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm font-bold text-light-text dark:text-dark-text">
                        {entry.totalPoints}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-light-text dark:text-dark-text">
                        {entry.correctResultCount}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-light-text dark:text-dark-text">
                        {entry.exactScoreCount}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-12">
            <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v4a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
            <h3 className="text-lg font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
              No Data Available
            </h3>
            <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
              {isLeagueTab
                ? 'No scored predictions found for this league yet.'
                : 'No predictions found for this competition yet.'}
            </p>
          </div>
        )}

        <div className="text-center mt-6 px-6 pb-4">
          <Link
            to={isLeagueTab && selectedTabInfo ? `/leagues/${selectedTabInfo.id}` : '/leaderboards'}
            className="text-sm font-medium"
            style={{ color: 'var(--highlight)' }}
          >
            {isLeagueTab ? 'View full league standings' : 'View full leaderboard'}
          </Link>
        </div>

      </div>
    </div>
  );
};

export default PartialLeaderboardWidget;
